import { useEffect } from 'react';
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  canonicalUrl,
  buildJsonLd,
  findPublicSeoPage,
} from '../../seo/publicPages';

/**
 * Sets document title + meta description/canonical/OG tags for public pages.
 * Also marks <html data-seo-ready="true"> so the postbuild prerender can wait.
 */
function useSeoMeta({ title, description, path, ogType = 'website' }) {
  useEffect(() => {
    if (!title && !description && !path) return undefined;

    const page = path ? findPublicSeoPage(path) : null;
    const resolvedTitle = title || page?.title;
    const resolvedDescription = description || page?.description;
    const canonical = path ? canonicalUrl(path) : `${SITE_URL}${window.location.pathname}`;

    if (resolvedTitle) {
      document.title = resolvedTitle;
    }

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        if (value != null) el.setAttribute(key, value);
      });
      return el;
    };

    if (resolvedTitle) {
      ensureMeta('meta[name="title"]', { name: 'title', content: resolvedTitle });
      ensureMeta('meta[property="og:title"]', { property: 'og:title', content: resolvedTitle });
      ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: resolvedTitle });
    }

    if (resolvedDescription) {
      ensureMeta('meta[name="description"]', {
        name: 'description',
        content: resolvedDescription,
      });
      ensureMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: resolvedDescription,
      });
      ensureMeta('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: resolvedDescription,
      });
    }

    ensureMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow' });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_OG_IMAGE });
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_OG_IMAGE });

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    if (page) {
      let jsonLd = document.getElementById('seo-json-ld');
      if (!jsonLd) {
        jsonLd = document.createElement('script');
        jsonLd.id = 'seo-json-ld';
        jsonLd.type = 'application/ld+json';
        document.head.appendChild(jsonLd);
      }
      jsonLd.textContent = JSON.stringify(buildJsonLd(page));
    }

    document.documentElement.setAttribute('data-seo-ready', 'true');

    return () => {
      document.documentElement.removeAttribute('data-seo-ready');
    };
  }, [title, description, path, ogType]);
}

export default useSeoMeta;
